import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ColDef, ICellRendererParams } from 'ag-grid-enterprise';
import { StatusPill } from './StatusPill';
import {
  ZfBox,
  ZfIcon,
  ZfPopover,
  ZfTextButton,
  ZfTooltip,
  ZfTypography,
  ZfStatusLabel,
  ZfStack,
  ZfIconButton,
} from '@zafire/react';
import { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { getStatusLabel } from './privateBankingStatusLabels';
import { formatDate } from '../../utils/dateUtils';
import { ServiceAgreementV2 } from './eBankingTempTypes';
import {
  PrivateBankingServiceDefinitionId,
  ServiceAgreementEditBlockedReason,
} from 'pco-product-service-internal-consumer';
import {
  BANKING_PRODUCTS,
  INVESTMENT_PRODUCTS,
  getProductDisplayName,
  getProductType,
} from './mappers/serviceAgreementToTreeMapper';

const editBlockedTooltipKeys: Record<ServiceAgreementEditBlockedReason, string> = {
  [ServiceAgreementEditBlockedReason.LodExpiryRequired]: 'pb.overview.actions.edit.blocked.lodExpiryRequired.tooltip',
  [ServiceAgreementEditBlockedReason.ManageAgreementsRoleRequired]: 'pb.overview.actions.edit.restricted.tooltip',
};

type ProductColDefOptions = {
  onEditProduct: (serviceAgreement: ServiceAgreementV2) => void | Promise<void>;
  onConfigureInvestmentProduct: (serviceAgreement: string) => void;
  statusFilterValues: string[];
  productTypeHeaderName?: string;
};

type EditActionCellProps = {
  serviceAgreement: ServiceAgreementV2;
  onEditProduct: (serviceAgreement: ServiceAgreementV2) => void | Promise<void>;
  onConfigureInvestmentProduct: (serviceAgreement: string) => void;
};

const PendingDraftPill: React.FC = () => {
  const { t } = useTranslation();
  return (
    <ZfBox style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
      <ZfStatusLabel presets="neutral-clock" label="Pending">
        <ZfStack slot="trailing">
          <span data-no-row-click style={{ display: 'flex', alignItems: 'center' }}>
            <ZfIconButton
              id="label-trailing"
              hierarchy="tertiary"
              size="extra-small"
              aria-label="More info"
              style={{ display: 'flex' }}
            >
              <ZfIcon aria-hidden="true" size="medium" name="info"></ZfIcon>
            </ZfIconButton>
          </span>
          <ZfPopover alignment="start" anchor="label-trailing">
            <ZfBox styles={{ textWrap: 'wrap' }}>
              <ZfTypography variant="label-tiny" color="normal">
                {t('pb.investmentproduct.pending.info')}
              </ZfTypography>
            </ZfBox>
          </ZfPopover>
        </ZfStack>
      </ZfStatusLabel>
    </ZfBox>
  );
};

const EditActionCell: React.FC<EditActionCellProps> = ({
  serviceAgreement,
  onEditProduct,
  onConfigureInvestmentProduct,
}) => {
  const { t } = useTranslation();
  const buttonWrapperRef = useRef<HTMLSpanElement | null>(null);
  const { canBeEdited, editBlockedReason } = serviceAgreement;

  return (
    <ZfBox
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        height: '100%',
        gap: 12,
        whiteSpace: 'normal',
      }}
    >
      <span data-no-row-click ref={buttonWrapperRef}>
        <ZfTextButton
          hierarchy="secondary"
          size="extra-small"
          data-testid="edit-product-button"
          disabled={!canBeEdited}
          onClick={() => {
            if (serviceAgreement.serviceDefinitionId === PrivateBankingServiceDefinitionId.ServiceModel) {
              onConfigureInvestmentProduct(serviceAgreement.serviceAgreementId || '');
              return;
            }
            void onEditProduct(serviceAgreement);
          }}
        >
          Edit
        </ZfTextButton>
      </span>
      {editBlockedReason && (
        <ZfTooltip
          text={t(editBlockedTooltipKeys[editBlockedReason])}
          anchor={() => buttonWrapperRef.current as HTMLElement}
          side="top"
        />
      )}
    </ZfBox>
  );
};

/**
 * Tree filtering normally keeps a group visible when any descendant matches. A child of a closed
 * product must therefore inherit Closed for filtering, otherwise it keeps the closed parent visible.
 * The cell renderer still receives the child's own agreementStatus and displays its real status.
 */
export const getTreeAwareAgreementStatus = (params: ValueGetterParams<ServiceAgreementV2>): string | undefined => {
  let node = params.node;

  while (node) {
    if (node.data?.agreementStatus === 'INACTIVE') {
      return 'INACTIVE';
    }
    node = node.parent;
  }

  return params.data?.agreementStatus;
};

/** Compares the cell's UTC calendar day with the day picked in the filter (local midnight). */
const activeSinceFilterComparator = (filterDateAtMidnight: Date, cellValue?: Date | null): number => {
  if (!cellValue || Number.isNaN(cellValue.getTime())) return -1;
  const cellDayMidnight = new Date(cellValue.getUTCFullYear(), cellValue.getUTCMonth(), cellValue.getUTCDate());
  if (cellDayMidnight < filterDateAtMidnight) return -1;
  if (cellDayMidnight > filterDateAtMidnight) return 1;
  return 0;
};

export const getProductColDefs = ({
  onEditProduct,
  onConfigureInvestmentProduct,
  statusFilterValues,
  productTypeHeaderName,
}: ProductColDefOptions): ColDef<ServiceAgreementV2>[] => [
  {
    colId: 'productType',
    headerName: productTypeHeaderName ?? 'Product type',
    flex: 1,
    minWidth: 180,
    filter: 'agSetColumnFilter',
    filterParams: {
      values: [BANKING_PRODUCTS, INVESTMENT_PRODUCTS],
      buttons: ['reset', 'apply'] as const,
      closeOnApply: true,
      suppressMiniFilter: true,
      cellRenderer: (params: ICellRendererParams) => params.value,
    },
    showRowGroup: true,
    valueGetter: (params) => {
      let node = params.node;

      while (node && node.level > 0) {
        node = node.parent;
      }

      return node?.key ?? getProductType(params.data);
    },
    cellRenderer: () => null,
    cellRendererSelector: (params) =>
      params.node.level === 0
        ? {
            component: 'agGroupCellRenderer',
            params: { suppressPadding: true },
          }
        : undefined,
  },
  {
    colId: 'product',
    headerName: 'Product',
    flex: 2.5,
    filter: 'agTextColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'] as const,
      closeOnApply: true,
      filterOptions: ['contains'],
      defaultOption: 'contains',
      maxNumConditions: 1,
    },
    valueGetter: (params) => {
      if (params.node?.level === 0) return '';

      return getProductDisplayName(params.data) || params.node?.key || '-';
    },
    cellRenderer: (params: ICellRendererParams<ServiceAgreementV2>) => (
      <span style={{ paddingLeft: Math.max(0, params.node.level - 1) * 20 }}>{params.value}</span>
    ),
  },
  {
    field: 'activeSince',
    headerName: 'Activation date',
    flex: 1,
    minWidth: 220,
    cellClass: 'ag-right-aligned-cell',
    filter: 'agDateColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'] as const,
      closeOnApply: true,
      maxNumConditions: 1,
      comparator: activeSinceFilterComparator,
    },
    valueFormatter: (params: ValueFormatterParams<ServiceAgreementV2, Date>) =>
      params.data ? formatDate(params.value ?? undefined) : '',
  },
  {
    field: 'agreementStatus',
    headerName: 'Status',
    flex: 1,
    minWidth: 220,
    filter: 'agSetColumnFilter',
    filterParams: {
      buttons: ['reset', 'apply'] as const,
      closeOnApply: true,
      values: statusFilterValues,
      valueFormatter: (params: ValueFormatterParams) => getStatusLabel(params.value),
    },
    filterValueGetter: getTreeAwareAgreementStatus,
    cellRenderer: (params: ICellRendererParams<ServiceAgreementV2>) => {
      if (!params.data) {
        return null;
      }

      return (
        <ZfBox style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <StatusPill status={params.value} clickable={true} />
          {params.data.draftServiceModel && <PendingDraftPill />}
        </ZfBox>
      );
    },
  },
  {
    headerName: 'Actions',
    field: undefined,
    flex: 1,
    sortable: false,
    minWidth: 55,
    resizable: false,
    filter: false,
    headerClass: 'ag-right-aligned-header',
    cellRenderer: (params: ICellRendererParams<ServiceAgreementV2>) => {
      const serviceAgreement = params.data;

      if (!serviceAgreement) {
        return null;
      }

      if (!serviceAgreement.canBeEdited && !serviceAgreement.editBlockedReason) {
        return null;
      }

      return (
        <EditActionCell
          serviceAgreement={serviceAgreement}
          onEditProduct={onEditProduct}
          onConfigureInvestmentProduct={onConfigureInvestmentProduct}
        />
      );
    },
  },
];
